import { useMemo } from 'react';
import { useGetExportSettingsMetadataQuery } from '../../../gql/queries/generated/GetExportSettingsMetadata';
import { secondsToTimecode } from '../utils';

const SORTED_METADATA_KEYS = [
  'format',
  'resolution',
  'frameRate',
  'bitRate',
  'colorSpace',
  'duration',
  'audio',
] as const;

// @todo (FCI-925): This will be removed when we have a way to correctly display the resolution and frame rate.
const SKIPPED_METADATA_KEYS: ReadonlyArray<string> = [
  'resolution',
  'frameRate',
];

export const useGetExportSettingsMetadata = ({
  format,
  presetId,
  entireSequence,
}: {
  format: string;
  presetId: string;
  entireSequence: boolean;
}) => {
  const { data } = useGetExportSettingsMetadataQuery({
    variables: {
      input: {
        presetId,
        entireSequence,
        format,
      },
    },
    context: {
      resolveWithUXP: true,
    },
    fetchPolicy: 'network-only',
  });

  const estimatedFileSizeMB =
    data?.getExportSettingsMetadata?.estimatedFileSizeMB ?? '';

  const frameRate = Number.parseFloat(
    data?.getExportSettingsMetadata?.frameRate ?? ''
  );

  const metadata = useMemo(
    () =>
      SORTED_METADATA_KEYS.reduce(
        (acc, key) => {
          let value = data?.getExportSettingsMetadata?.[key] ?? '';
          value =
            key === 'duration'
              ? secondsToTimecode(Number(value), frameRate)
              : value;
          value =
            key === 'bitRate' && !Number.parseFloat(`${value}`) ? '' : value;

          if (value && !SKIPPED_METADATA_KEYS.includes(key)) {
            acc.push({ key, value });
          }
          return acc;
        },
        [] as {
          key: (typeof SORTED_METADATA_KEYS)[number];
          value: string | number;
        }[]
      ),
    [data?.getExportSettingsMetadata, frameRate]
  );

  return {
    estimatedFileSizeMB,
    metadata,
  };
};
